import styled, { keyframes } from "styled-components";

// ===== ANIMATIONS =====
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -8px, 0);
  }
  70% {
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
`;

const timerPulse = keyframes`
  0%, 100% {
    opacity: 1;
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
  50% {
    opacity: 0.8;
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.5);
  }
`;

const warningPulse = keyframes`
  0%, 100% {
    background: linear-gradient(135deg, #ff6b6b, #ee5a24);
    transform: scale(1);
  }
  50% {
    background: linear-gradient(135deg, #ee5a24, #d63031);
    transform: scale(1.02);
  }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
`;

// ===== CORE CONTAINER =====
export const Container = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
`;

// ===== SIDEBAR =====
export const Sidebar = styled.div`
  width: 380px;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-right: 1px solid rgba(226, 232, 240, 0.8);

  @media (max-width: 768px) {
    width: ${(props) => (props.$isOpen ? "380px" : "0")};
    position: absolute;
    z-index: 10;
    height: 100%;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
`;

export const Logo = styled.div`
  font-weight: 800;
  font-size: 24px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  text-transform: capitalize;
`;

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  font-size: 14px;

  &:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.35);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: rotate(-10deg);
  }
`;

export const NotificationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(
    135deg,
    rgb(68, 239, 71) 0%,
    rgb(68, 220, 38) 100%
  );
  border: none;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  font-size: 14px;

   &:hover {
    background: linear-gradient(
      135deg,
      rgb(68, 220, 38) 0%,
      rgb(28, 185, 31) 100%
    );
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(68, 239, 94, 0.35);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(68, 239, 85, 0.25);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: rotate(-10deg);
  }
`;

// ===== HISTORY BUTTON =====

export const HistoryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(
    135deg,
    rgb(68, 239, 71) 0%,
    rgb(68, 220, 38) 100%
  );
  border: none;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(94, 239, 68, 0.25);
  font-size: 14px;
  margin-left:20px;

  &:hover {
    background: linear-gradient(
      135deg,
      rgb(68, 220, 38) 0%,
      rgb(28, 185, 31) 100%
    );
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(68, 239, 94, 0.35);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(68, 239, 85, 0.25);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: rotate(-10deg);
  }
`;

// ===== CONNECTION STATUS =====
export const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.$connected ? "#25d366" : "#ef4444")};

  svg {
    animation: ${(props) => (props.$connected ? "none" : pulse)} 2s infinite;
  }
`;

// ===== SEARCH =====
export const SearchContainer = styled.div`
  padding: 16px 24px;
  background: #f8fafc;
  display: flex;
  justify-content: space-between;
`;

export const SearchInput = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 16px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.6);
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #25d366;
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.15);
  }

  input {
    border: none;
    outline: none;
    width: 100%;
    margin-left: 12px;
    font-size: 15px;
    color: #1e293b;
    font-weight: 500;

    &::placeholder {
      color: #94a3b8;
      font-weight: 400;
    }
  }
`;

// ===== CONTACTS LIST =====
export const ContactsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  }
`;

export const Contact = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: ${(props) =>
    props.$active
      ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
      : "transparent"};
  border-left: ${(props) =>
    props.$active ? "4px solid #25d366" : "4px solid transparent"};

  &:hover {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    transform: translateX(4px);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 24px;
    right: 24px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      #e2e8f0 50%,
      transparent 100%
    );
  }
`;

export const StoreList = styled.div`
  height: 20rem;
  overflow-y: scroll;
`;

export const ModalCloseButton = styled.div`
  cursor: pointer;
  color: red;
`;

// ===== INACTIVE CONTACT STYLING =====
export const ContactItemInactive = styled(Contact)`
  background: ${(props) =>
    props.$active
      ? "linear-gradient(135deg, #fef3cd 0%, #fde68a 100%)"
      : "linear-gradient(135deg, #fef9e7 0%, #fef3cd 100%)"};
  border-left: 4px solid #f59e0b;
  opacity: 0.8;

  &:hover {
    background: linear-gradient(135deg, #fde68a 0%, #fcd34d 100%);
    transform: translateX(2px);
  }
`;

export const Avatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: 3px solid #25d366;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  color: #25d366;
  background: white;
  margin-right: 16px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.2);
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(37, 211, 102, 0.3);
  }

  &.has-update {
    animation: ${pulse} 1s ease-in-out;
  }

  &.inactive {
    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
    border-color: #9ca3af;
    opacity: 0.7;
    color: white;
  }
`;

// ===== INACTIVE INDICATORS =====
export const InactiveContactBadge = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  border-radius: 50%;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
  animation: ${pulse} 2s infinite;
`;

export const ContactInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 12px;
`;

export const ContactName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  line-height: 1.3;

  &.inactive::after {
    content: " (Inactive)";
    color: #f59e0b;
    font-size: 0.8em;
    font-weight: 500;
    margin-left: 8px;
    background-color: rgba(245, 158, 11, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
  }
`;

export const ContactStatus = styled.div`
  font-size: 14px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
  line-height: 1.3;

  &.inactive {
    color: #92400e;
    font-style: italic;
  }
`;

export const ContactMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

export const ContactTimestamp = styled.div`
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  margin-bottom: 4px;
  font-weight: 500;
`;

export const ContactRightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 8px;
  min-width: 70px;
  height: 52px;
  position: relative;
`;

export const ReactivateHint = styled.div`
  font-size: 10px;
  color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
  text-align: center;
  font-weight: 500;
  animation: ${fadeIn} 0.3s ease;
`;

export const UnreadIndicator = styled.div`
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
  border-radius: 12px;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
  animation: ${pulse} 2s infinite;
`;

// ===== CHAT CONTAINER =====
export const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
`;

export const EmptyChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  color: #64748b;
  text-align: center;
  padding: 40px;
`;

export const NoChatSelected = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  color: #64748b;
  text-align: center;
  padding: 40px;

  h3 {
    font-size: 28px;
    font-weight: 700;
    margin: 16px 0 8px 0;
    background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    font-size: 16px;
    color: #64748b;
    margin: 0;
  }

  svg {
    opacity: 0.3;
  }
`;

export const EmptyChatIcon = styled.div`
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.6);
`;

export const EmptyChatTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
`;

export const EmptyChatText = styled.p`
  font-size: 16px;
  max-width: 500px;
  line-height: 1.6;
  margin-bottom: 30px;
  color: #64748b;
  font-weight: 400;
`;

// ===== CHAT HEADER =====
export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  z-index: 2;
  position: relative;

  &.inactive {
    background: linear-gradient(135deg, #fef3cd 0%, #fde68a 100%);
    border-bottom: 2px solid #f59e0b;
  }
`;

export const ChatInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 16px;
`;

export const ChatName = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: #1e293b;
  line-height: 1.3;

  &.inactive::after {
    content: " (Inactive - Click to reactivate)";
    color: #f59e0b;
    font-size: 0.85em;
    font-weight: 500;
  }
`;

export const ChatStatus = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
  margin-top: 2px;
`;

export const ChatControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ControlButton = styled.button`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid rgba(226, 232, 240, 0.8);
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
    color: #475569;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const FilterButton = styled.button`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid rgba(226, 232, 240, 0.8);
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
    color: #475569;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ===== TOGGLE COMPONENTS =====
export const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ToggleLabels = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  color: ${(props) => (props.$active ? "#25d366" : "#64748b")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  opacity: ${(props) => (props.$active ? "1" : "0.7")};

  &:hover {
    opacity: 1;
  }
`;

export const ToggleSwitch = styled.div`
  position: relative;
  display: inline-block;
`;

export const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;

export const ToggleSlider = styled.label`
  position: relative;
  display: inline-block;
  width: 64px;
  height: 32px;
  background: ${(props) =>
    props.$isAgent
      ? "linear-gradient(135deg, #25d366 0%, #128c7e 100%)"
      : "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)"};
  border-radius: 32px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${(props) =>
    props.$isAgent
      ? "0 4px 12px rgba(37, 211, 102, 0.3)"
      : "0 2px 8px rgba(0, 0, 0, 0.1)"};
  border: 2px solid ${(props) => (props.$isAgent ? "#25d366" : "transparent")};

  &:before {
    content: "";
    position: absolute;
    height: 24px;
    width: 24px;
    left: ${(props) => (props.$isAgent ? "36px" : "4px")};
    bottom: 2px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.$isAgent
        ? "0 6px 16px rgba(37, 211, 102, 0.4)"
        : "0 4px 12px rgba(0, 0, 0, 0.15)"};
  }

  &:active:before {
    width: 28px;
  }

  ${ToggleInput}:focus + & {
    outline: 2px solid #25d366;
    outline-offset: 2px;
  }
`;

// ===== INACTIVITY TIMER STYLES =====
export const InactivityTimerContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${(props) =>
    props.$warning
      ? "linear-gradient(135deg, #ff6b6b, #ee5a24)"
      : "linear-gradient(135deg, #ff9500, #ff6b6b)"};
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  z-index: 1000;
  animation: ${(props) => (props.$warning ? warningPulse : timerPulse)} 1s
    infinite;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }
`;

export const TimerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${(props) => (props.$warning ? shake : "none")} 0.5s infinite;
`;

export const TimerText = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

// ===== INACTIVE CHAT OVERLAY STYLES =====
export const InactiveChatOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  color: #1e293b;
  padding: 20px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease;
`;

export const InactiveIcon = styled.div`
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-radius: 50%;
  padding: 20px;
  margin-bottom: 20px;
  animation: ${bounce} 2s infinite;
  box-shadow: 0 8px 32px rgba(245, 158, 11, 0.3);
`;

export const InactiveTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #f59e0b;
`;

export const InactiveMessage = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 300px;
`;

export const ReactivateButton = styled.button`
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);

  &:hover {
    background: linear-gradient(135deg, #d97706, #b45309);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }
`;

// ===== CHAT MESSAGES STYLES =====
export const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  }
`;

export const LoadingMessages = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #64748b;
  gap: 12px;

  svg {
    animation: ${spin} 1s linear infinite;
  }
`;

export const EmptyChat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #9ca3af;
  gap: 12px;

  svg {
    opacity: 0.5;
  }
`;

export const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$sent ? "flex-end" : "flex-start")};
  margin-bottom: 20px;
  max-width: 85%;
  align-self: ${(props) => (props.$sent ? "flex-end" : "flex-start")};
`;

export const Message = styled.div`
  background: ${(props) =>
    props.$sent
      ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"};
  border-radius: ${(props) =>
    props.$sent ? "18px 18px 4px 18px" : "18px 18px 18px 4px"};
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 80px;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  position: relative;
  border: 1px solid
    ${(props) =>
      props.$sent ? "rgba(37, 211, 102, 0.2)" : "rgba(226, 232, 240, 0.6)"};
  transition: all 0.2s ease;

  &.new-message {
    animation: ${fadeIn} 0.5s ease;
    transform: translateY(0);
  }

  &.sending {
    opacity: 0.7;
    background: ${(props) =>
      props.$sent
        ? "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)"
        : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)"};
  }

  &.failed {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border-color: #fca5a5;
    animation: ${pulse} 1s infinite;
  }

  &.system {
    align-self: center;
    background: rgba(148, 163, 184, 0.1);
    color: #64748b;
    font-style: italic;
    text-align: center;
    max-width: 80%;
  }

  &::after {
    content: "";
    position: absolute;
    ${(props) =>
      props.$sent
        ? `
      bottom: 0;
      right: -8px;
      width: 0;
      height: 0;
      border-left: 8px solid #dcfce7;
      border-top: 8px solid transparent;
      `
        : `
      bottom: 0;
      left: -8px;
      width: 0;
      height: 0;
      border-right: 8px solid #ffffff;
      border-top: 8px solid transparent;
      `}
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

export const SystemMessage = styled.div`
  align-self: center;
  background: rgba(148, 163, 184, 0.1);
  color: #64748b;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-style: italic;
  margin: 8px 0;
  text-align: center;
  max-width: 80%;
  animation: ${fadeIn} 0.3s ease;
`;

export const MessageContent = styled.div`
  font-size: 15px;
  color: #1e293b;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.5;
  margin-bottom: 8px;
  font-weight: 400;
`;

export const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
`;

export const MessageTime = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
`;

export const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MessageStatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;

    &.status-sending {
      color: #94a3b8;
      animation: ${spin} 1s linear infinite;
    }

    &.status-sent {
      color: #94a3b8;
    }

    &.status-delivered {
      color: #3b82f6;
    }

    &.status-read {
      color: #25d366;
    }

    &.status-failed {
      color: #ef4444;
      animation: ${pulse} 1s infinite;
    }
  }
`;

export const DateDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 24px 0;

  span {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(226, 232, 240, 0.6);
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`;

// ===== INTERACTIVE MESSAGES =====
export const InteractiveMessage = styled.div`
  background: ${(props) =>
    props.$sent
      ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"};
  border-radius: 18px;
  padding: 16px 20px;
  margin: 4px 0;
  max-width: 420px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid
    ${(props) =>
      props.$sent ? "rgba(37, 211, 102, 0.2)" : "rgba(226, 232, 240, 0.6)"};
  color: #1e293b;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
`;

export const InteractiveMessageComponent = styled.div`
  background: ${(props) =>
    props.$sent
      ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"};
  border-radius: 18px;
  padding: 16px;
  margin: 4px 0;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid
    ${(props) =>
      props.$sent ? "rgba(37, 211, 102, 0.2)" : "rgba(226, 232, 240, 0.6)"};
  animation: ${fadeIn} 0.3s ease;
`;

export const InteractiveMessageHeader = styled.div`
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
  font-size: 16px;
  line-height: 1.4;
`;

export const MessageHeader = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 8px;
  color: #1e293b;
`;

export const InteractiveMessageBody = styled.div`
  color: #475569;
  margin-bottom: 16px;
  font-size: 15px;
  line-height: 1.5;
  font-weight: 400;
`;

export const MessageBody = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
  margin-bottom: 12px;
`;

export const InteractiveMessageFooter = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 12px;
  font-style: italic;
  font-weight: 400;
`;

export const MessageFooter = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
  margin-top: 8px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
  width: 100%;
`;

export const MessageButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

export const WhatsAppButton = styled.button`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid rgba(226, 232, 240, 0.8);
  color: #1e293b;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
  word-break: break-word;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(37, 211, 102, 0.1),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-color: #25d366;
    color: #166534;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(37, 211, 102, 0.2);

    &:before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
    color: white;
    border-color: #25d366;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    border-color: #cbd5e1;
    color: #94a3b8;
    transform: none;
    box-shadow: none;
  }

  &:focus {
    outline: 3px solid rgba(37, 211, 102, 0.3);
    outline-offset: 2px;
  }
`;

export const InteractiveButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: rgba(37, 211, 102, 0.1);
    border-color: #25d366;
    color: #166534;
  }

  &:active {
    background: #25d366;
    color: white;
  }
`;

// ===== IMAGE MESSAGES =====
export const ImageMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeIn} 0.3s ease;
`;

export const MessageImage = styled.img`
  max-width: 300px;
  max-height: 300px;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
`;

export const ImageCaption = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
`;

// ===== TYPING INDICATOR =====
export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #64748b;
  font-size: 12px;
  font-style: italic;
  padding: 8px 16px;

  &::after {
    content: "...";
    animation: ${pulse} 1.5s infinite;
  }
`;

// ===== CHAT INPUT =====
export const ChatInput = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  position: relative;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  ${(props) =>
    props.$disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  `}
`;

export const InputField = styled.div`
  flex: 1;
  background: white;
  border-radius: 24px;
  padding: 12px 18px;
  margin: 0 12px;
  display: flex;
  align-items: center;
  border: 2px solid rgba(226, 232, 240, 0.6);
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #25d366;
    box-shadow: 0 4px 16px rgba(37, 211, 102, 0.15);
  }

  textarea,
  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 15px;
    line-height: 20px;
    color: #1e293b;
    font-weight: 400;
    resize: none;

    &::placeholder {
      color: #94a3b8;
    }

    &:disabled {
      background: transparent;
      color: #94a3b8;
    }
  }
`;

export const InputFieldDisabled = styled(InputField)`
  opacity: 0.6;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-color: #cbd5e1;

  textarea,
  input {
    color: #94a3b8;
    cursor: not-allowed;

    &::placeholder {
      color: #cbd5e1;
    }
  }
`;

export const SendButton = styled.button`
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: none;
  color: white;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #128c7e 0%, #0f7a6b 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
  }

  &:disabled {
    background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

export const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 8px;
  margin-right: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(100, 116, 139, 0.1);
    color: #475569;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

export const ButtonMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

export const MessageButton = styled.button`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  padding: 12px 18px;
  font-size: 14px;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;

  &:hover {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
    border-color: #25d366;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
    color: white;
    transform: translateY(0);
  }
`;

export const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
  font-size: 16px;
  color: #64748b;
  position: relative;
  font-weight: 500;

  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    border: 3px solid rgba(37, 211, 102, 0.2);
    border-top-color: #25d366;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;

// ===== UTILITY CLASSES =====
export const AnimatePulse = styled.div`
  animation: ${pulse} 2s infinite;
`;

export const AnimateFadeIn = styled.div`
  animation: ${fadeIn} 0.3s ease;
`;

export const AnimateSpin = styled.div`
  animation: ${spin} 1s linear infinite;
`;

export const AnimateBounce = styled.div`
  animation: ${bounce} 2s infinite;
`;

// ===== RESPONSIVE STYLES =====
export const ResponsiveWrapper = styled.div`
  @media (max-width: 768px) {
    ${ChatContainer} {
      width: 100%;
    }

    ${Sidebar} {
      position: ${(props) => (props.$sidebarOpen ? "fixed" : "absolute")};
      left: ${(props) => (props.$sidebarOpen ? "0" : "-100%")};
      z-index: 1000;
    }

    ${InactivityTimerContainer} {
      top: 60px;
      right: 16px;
      font-size: 11px;
      padding: 6px 10px;
    }

    ${InactiveChatOverlay} {
      padding: 16px;
    }

    ${ChatMessages} {
      padding: 16px;
    }

    ${ChatInput} {
      padding: 12px 16px;
    }
  }

  @media (max-width: 480px) {
    ${MessageGroup} {
      max-width: 95%;
    }

    ${Message} {
      padding: 10px 14px;
      font-size: 14px;
    }

    ${InteractiveMessage} {
      max-width: 95%;
      padding: 14px 16px;
    }

    ${WhatsAppButton} {
      padding: 12px 16px;
      font-size: 13px;
      min-height: 44px;
    }
  }
`;

// ===== THEME VARIANTS =====
export const DarkTheme = styled.div`
  ${Container} {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  }

  ${Sidebar} {
    background: #1e293b;
    border-right-color: #334155;
  }

  ${Message} {
    background: ${(props) =>
      props.$sent
        ? "linear-gradient(135deg, #166534 0%, #15803d 100%)"
        : "linear-gradient(135deg, #374151 0%, #1f2937 100%)"};
    color: white;
    border-color: ${(props) => (props.$sent ? "#166534" : "#374151")};
  }

  ${ChatMessages} {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  }
`;

export const InactiveContactLabel = styled.span`
  font-size: 10px;
  color: #f59e0b;
  margin-left: 6px;
  font-weight: 500;
  background: rgba(245, 158, 11, 0.1);
  padding: 2px 6px;
  border-radius: 8px;
  animation: ${fadeIn} 0.3s ease;
`;

export default {
  Container,
  Sidebar,
  SidebarHeader,
  Logo,
  LogoutButton,
  ConnectionStatus,
  SearchContainer,
  SearchInput,
  ContactsList,
  Contact,
  ContactItemInactive,
  Avatar,
  InactiveContactBadge,
  ContactInfo,
  ContactName,
  ContactStatus,
  ContactMeta,
  ContactTimestamp,
  ContactRightSection,
  ReactivateHint,
  UnreadIndicator,
  ChatContainer,
  EmptyChatContainer,
  NoChatSelected,
  EmptyChatIcon,
  EmptyChatTitle,
  EmptyChatText,
  ChatHeader,
  ChatInfo,
  ChatName,
  ChatStatus,
  ChatControls,
  ControlButton,
  ToggleContainer,
  ToggleSwitch,
  ToggleInput,
  ToggleSlider,
  ToggleLabels,
  ToggleLabel,
  ChatMessages,
  LoadingMessages,
  EmptyChat,
  MessageGroup,
  Message,
  SystemMessage,
  MessageContent,
  MessageMeta,
  MessageTime,
  StatusIndicator,
  MessageStatusIcon,
  DateDivider,
  ChatInput,
  InputField,
  InputFieldDisabled,
  SendButton,
  MobileMenuButton,
  ButtonMessage,
  MessageButton,
  LoadingIndicator,
  InteractiveMessage,
  InteractiveMessageComponent,
  ButtonContainer,
  MessageButtonsContainer,
  WhatsAppButton,
  InteractiveButton,
  InteractiveMessageHeader,
  MessageHeader,
  InteractiveMessageBody,
  MessageBody,
  InteractiveMessageFooter,
  MessageFooter,
  ImageMessage,
  MessageImage,
  ImageCaption,
  SystemMessage,
  TypingIndicator,
  InactivityTimerContainer,
  TimerIcon,
  TimerText,
  InactiveChatOverlay,
  InactiveIcon,
  InactiveTitle,
  InactiveMessage,
  ReactivateButton,
  InactiveContactLabel,
  AnimatePulse,
  AnimateFadeIn,
  AnimateSpin,
  AnimateBounce,
  ResponsiveWrapper,
  DarkTheme,
};

